import { AutocompleteGenericEntity, DomainCode } from '@australiangreens/ag-internal-components';
import { RuleProperties } from '@react-awesome-query-builder/mui';

import { getGeometries } from '@/apis/geometries';
import { GeometryIdsFilter, isTemplatePlaceholder } from '@/types';
import { PRETTY_GEOM_TYPES } from '@/constants/filterCriteria/location';
import { FilterMapper, ValidationContext } from './filterMapperTypes';

export const loadGeometries = async ({
  token,
  userSub,
  domainCode,
  geometryIds,
}: {
  token: string;
  userSub: string;
  domainCode: DomainCode;
  geometryIds: number[];
}) => {
  const geometries = await getGeometries(token, geometryIds, { sub: userSub, domainCode });
  const geometriesMapped = geometries.map((x) => {
    return {
      id: x.id,
      geometryType: x.geometryType,
      label: `${x.label} [${PRETTY_GEOM_TYPES[x.geometryType]}]`,
    };
  });
  return { geometries: geometriesMapped, isMissing: geometries.length !== geometryIds.length };
};

export const GeometryIdsMapper: FilterMapper = {
  checkIsIncomplete({ value }) {
    const isNoGeometrySelected = value[0].value.length === 0;
    return isNoGeometrySelected;
  },

  toFilter({ value, operator }: RuleProperties): GeometryIdsFilter {
    if (!value) throw new Error('Missing value');
    if (value.length !== 1) throw new Error('Invalid value. Expected array of length 1');

    const nestedValue = value[0].value;
    let mappedValue;
    if (isTemplatePlaceholder(nestedValue)) {
      mappedValue = nestedValue;
    } else {
      const geometries = nestedValue as AutocompleteGenericEntity[];

      mappedValue = geometries.map(({ id }) => {
        if (typeof id !== 'number') throw new Error('Invalid value. Expect array of numbers');
        return id;
      });
    }

    return {
      name: 'geometryIds',
      value: mappedValue,
      operator: operator === 'any in' ? 'lives in' : operator,
    } as GeometryIdsFilter;
  },

  async fromFilter(filter: GeometryIdsFilter, { userSub, domainCode, token }: ValidationContext) {
    if (isTemplatePlaceholder(filter.value)) {
      return {
        field: filter.name,
        operator: filter.operator ?? 'lives in',
        value: [
          {
            value: filter.value,
            errors: [],
          },
        ],
      };
    }

    const { geometries, isMissing } = await loadGeometries({
      token,
      geometryIds: filter.value,
      userSub,
      domainCode,
    });

    return {
      field: filter.name,
      operator: filter.operator ?? 'lives in',
      value: [
        {
          value: geometries,
          errors: isMissing ? [{ field: 'locations', message: 'Missing locations' }] : [],
        },
      ],
    };
  },
};
